import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Volume2, FileText, AlertCircle } from 'lucide-react';

const TermsOfService = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-sm border-b border-indigo-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center space-x-3">
            <div className="bg-indigo-600 p-2 rounded-lg">
              <Volume2 className="h-6 w-6 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900">Speakify</h1>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Card className="shadow-xl border-0 bg-white/90 backdrop-blur-sm">
          <CardHeader>
            <div className="flex items-center space-x-3 mb-4">
              <FileText className="h-8 w-8 text-indigo-600" />
              <CardTitle className="text-3xl text-gray-900">Terms of Service</CardTitle>
            </div>
            <p className="text-gray-600">Last Updated: November 5, 2025</p>
          </CardHeader>

          <CardContent className="space-y-8 text-gray-700">
            {/* Introduction */}
            <section>
              <p className="text-lg leading-relaxed">
                These Terms of Service ("Terms") govern your use of Speakify, a Shopify application developed by Vortex AI Advisory. By installing and using Speakify, you agree to be bound by these Terms.
              </p>
            </section>

            {/* Acceptance of Terms */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-3">1. Acceptance of Terms</h2>
              
              <div className="space-y-3 ml-8">
                <p className="leading-relaxed">
                  By installing, accessing, or using Speakify, you agree to:
                </p>
                <ul className="list-disc ml-6 space-y-1">
                  <li>These Terms of Service</li>
                  <li>Our Privacy Policy</li>
                  <li>Shopify's Terms of Service</li>
                  <li>All applicable laws and regulations</li>
                </ul>
                <p className="leading-relaxed mt-3">
                  If you do not agree to these Terms, you must not install or use Speakify.
                </p>
              </div>
            </section>

            {/* Description of Service */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-3">2. Description of Service</h2>
              
              <div className="space-y-3 ml-8">
                <p className="leading-relaxed">
                  Speakify provides the following services for Shopify merchants:
                </p>
                <ul className="list-disc ml-6 space-y-2">
                  <li><strong>AI Audio Generation:</strong> Convert product descriptions to audio using text-to-speech technology (powered by ElevenLabs)</li>
                  <li><strong>Custom Voice Cloning:</strong> Create custom brand voices from audio samples</li>
                  <li><strong>Text Enhancement:</strong> Improve product descriptions using Claude AI (Anthropic)</li>
                  <li><strong>Audio Widget:</strong> Embed audio players on product pages</li>
                  <li><strong>Audio Management:</strong> Manage and organize product audio descriptions</li>
                </ul>
              </div>
            </section>

            {/* Eligibility */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-3">3. Eligibility</h2>
              
              <div className="space-y-3 ml-8">
                <p className="leading-relaxed">
                  To use Speakify, you must:
                </p>
                <ul className="list-disc ml-6 space-y-1">
                  <li>Be at least 18 years old</li>
                  <li>Have a valid Shopify store</li>
                  <li>Have the legal authority to bind your business to these Terms</li>
                  <li>Comply with all applicable laws in your jurisdiction</li>
                </ul>
              </div>
            </section>

            {/* User Obligations */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-3">4. Your Obligations</h2>
              
              <div className="space-y-3 ml-8">
                <p className="leading-relaxed">
                  You agree to:
                </p>
                <ul className="list-disc ml-6 space-y-2">
                  <li><strong>Accurate Information:</strong> Provide accurate and complete information</li>
                  <li><strong>Security:</strong> Maintain the security of your Shopify store credentials</li>
                  <li><strong>Lawful Use:</strong> Use Speakify only for lawful purposes</li>
                  <li><strong>Content Ownership:</strong> Ensure you have rights to all content you submit for audio generation</li>
                  <li><strong>Voice Samples:</strong> Ensure you have permission to use any voice samples for cloning</li>
                  <li><strong>Compliance:</strong> Comply with all applicable laws, including copyright and trademark laws</li>
                </ul>
              </div>
            </section>

            {/* Prohibited Uses */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-3">5. Prohibited Uses</h2>
              
              <div className="space-y-3 ml-8">
                <p className="leading-relaxed">
                  You may NOT use Speakify to:
                </p>
                <ul className="list-disc ml-6 space-y-2">
                  <li>Generate audio for illegal, harmful, or offensive content</li>
                  <li>Clone voices without proper authorization or consent</li>
                  <li>Impersonate individuals without permission</li>
                  <li>Violate intellectual property rights</li>
                  <li>Spam, harass, or abuse others</li>
                  <li>Attempt to reverse engineer or copy the service</li>
                  <li>Overload or interfere with our systems</li>
                  <li>Resell or redistribute our services without permission</li>
                </ul>
              </div>
            </section>

            {/* Intellectual Property */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-3">6. Intellectual Property</h2>
              
              <div className="space-y-4 ml-8">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Our Rights</h3>
                  <p className="leading-relaxed">
                    Speakify, including its software, design, features, and documentation, is owned by Vortex AI Advisory and protected by intellectual property laws. You may not copy, modify, or distribute our intellectual property without written permission.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Your Content</h3>
                  <p className="leading-relaxed">
                    You retain ownership of all content you submit (product descriptions, voice samples, etc.). By using Speakify, you grant us a limited license to:
                  </p>
                  <ul className="list-disc ml-6 mt-2 space-y-1">
                    <li>Process your content to generate audio</li>
                    <li>Store generated audio files on your behalf</li>
                    <li>Use third-party AI services (ElevenLabs, Claude) to provide services</li>
                  </ul>
                  <p className="leading-relaxed mt-2">
                    This license ends when you uninstall Speakify or delete the specific content.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Generated Audio</h3>
                  <p className="leading-relaxed">
                    You own the audio files generated using Speakify. You may use them on your Shopify store and other marketing materials.
                  </p>
                </div>
              </div>
            </section>

            {/* Fees and Payment */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-3">7. Fees and Payment</h2>
              
              <div className="space-y-3 ml-8">
                <p className="leading-relaxed">
                  Speakify may offer different pricing plans:
                </p>
                <ul className="list-disc ml-6 space-y-2">
                  <li><strong>Free Tier:</strong> Limited features with usage restrictions</li>
                  <li><strong>Paid Plans:</strong> Additional features and higher usage limits</li>
                </ul>
                <p className="leading-relaxed mt-3">
                  If you subscribe to a paid plan:
                </p>
                <ul className="list-disc ml-6 mt-2 space-y-1">
                  <li>Fees are processed through Shopify's billing system</li>
                  <li>You authorize us to charge the agreed fees</li>
                  <li>Fees are non-refundable except as required by law</li>
                  <li>We may change fees with 30 days' notice</li>
                </ul>
              </div>
            </section>

            {/* Third-Party Services */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-3">8. Third-Party Services</h2>
              
              <div className="space-y-3 ml-8">
                <p className="leading-relaxed">
                  Speakify uses the following third-party services:
                </p>
                <ul className="list-disc ml-6 space-y-2">
                  <li><strong>ElevenLabs:</strong> Text-to-speech and voice cloning</li>
                  <li><strong>Anthropic Claude:</strong> AI text enhancement</li>
                  <li><strong>AWS S3:</strong> Audio file storage</li>
                </ul>
                <p className="leading-relaxed mt-3">
                  Your use of these services through Speakify is also subject to their respective terms of service. We are not responsible for any issues arising from these third-party services.
                </p>
              </div>
            </section>

            {/* Disclaimers */}
            <section>
              <div className="flex items-center space-x-2 mb-3">
                <AlertCircle className="h-6 w-6 text-amber-600" />
                <h2 className="text-2xl font-semibold text-gray-900">9. Disclaimers</h2>
              </div>
              
              <div className="space-y-3 ml-8">
                <p className="leading-relaxed uppercase font-semibold">
                  Speakify is provided "as is" without warranties of any kind.
                </p>
                <p className="leading-relaxed">
                  We disclaim all warranties, express or implied, including:
                </p>
                <ul className="list-disc ml-6 space-y-1">
                  <li>Merchantability and fitness for a particular purpose</li>
                  <li>Non-infringement of third-party rights</li>
                  <li>Uninterrupted or error-free service</li>
                  <li>Accuracy or reliability of AI-generated content</li>
                  <li>Compatibility with all Shopify themes</li>
                </ul>
                <p className="leading-relaxed mt-3">
                  You use Speakify at your own risk. We do not guarantee specific results or outcomes.
                </p>
              </div>
            </section>

            {/* Limitation of Liability */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-3">10. Limitation of Liability</h2>
              
              <div className="space-y-3 ml-8">
                <p className="leading-relaxed uppercase font-semibold">
                  To the maximum extent permitted by law:
                </p>
                <ul className="list-disc ml-6 space-y-2">
                  <li>We shall not be liable for any indirect, incidental, special, consequential, or punitive damages</li>
                  <li>This includes lost profits, data loss, or business interruption</li>
                  <li>Our total liability shall not exceed the amount you paid us in the 12 months before the claim</li>
                  <li>Some jurisdictions do not allow limitation of liability, so this may not apply to you</li>
                </ul>
              </div>
            </section>

            {/* Indemnification */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-3">11. Indemnification</h2>
              
              <div className="space-y-3 ml-8">
                <p className="leading-relaxed">
                  You agree to indemnify and hold harmless Vortex AI Advisory, its affiliates, and personnel from any claims, damages, losses, or expenses (including legal fees) arising from:
                </p>
                <ul className="list-disc ml-6 space-y-1">
                  <li>Your use of Speakify</li>
                  <li>Your violation of these Terms</li>
                  <li>Your violation of any laws or third-party rights</li>
                  <li>Content you submit for audio generation</li>
                </ul>
              </div>
            </section>

            {/* Termination */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-3">12. Termination</h2>
              
              <div className="space-y-4 ml-8">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">By You</h3>
                  <p className="leading-relaxed">
                    You may terminate your use of Speakify at any time by uninstalling the app from your Shopify store. After 48 hours, all your data will be permanently deleted.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">By Us</h3>
                  <p className="leading-relaxed">
                    We may suspend or terminate your access to Speakify:
                  </p>
                  <ul className="list-disc ml-6 mt-2 space-y-1">
                    <li>If you violate these Terms</li>
                    <li>If your use poses security risks</li>
                    <li>If required by law</li>
                    <li>For non-payment (if applicable)</li>
                  </ul>
                  <p className="leading-relaxed mt-2">
                    We will provide notice when possible, but may terminate immediately for serious violations.
                  </p>
                </div>
              </div>
            </section>

            {/* Changes to Terms */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-3">13. Changes to Terms</h2>
              
              <div className="space-y-3 ml-8">
                <p className="leading-relaxed">
                  We may update these Terms from time to time. Changes will be effective:
                </p>
                <ul className="list-disc ml-6 space-y-1">
                  <li>When we update the "Last Updated" date</li>
                  <li>After we notify you through the app or email</li>
                </ul>
                <p className="leading-relaxed mt-3">
                  Your continued use of Speakify after changes constitutes acceptance of the updated Terms. If you don't agree, you must stop using the service.
                </p>
              </div>
            </section>

            {/* Governing Law */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-3">14. Governing Law</h2>
              
              <div className="space-y-3 ml-8">
                <p className="leading-relaxed">
                  These Terms shall be governed by and construed in accordance with the laws of [Your Jurisdiction], without regard to its conflict of law provisions.
                </p>
                <p className="leading-relaxed mt-3">
                  Any disputes arising from these Terms or your use of Speakify shall be resolved in the courts of [Your Jurisdiction].
                </p>
              </div>
            </section>

            {/* Contact Information */}
            <section className="bg-indigo-50 p-6 rounded-lg border border-indigo-100">
              <h2 className="text-2xl font-semibold text-gray-900 mb-3">15. Contact Us</h2>
              
              <div className="space-y-2 ml-8">
                <p className="leading-relaxed">
                  If you have questions about these Terms, please contact us:
                </p>
                <div className="mt-4 space-y-1">
                  <p><strong>Email:</strong> <a href="mailto:question2abhi@gmail.com" className="text-indigo-600 hover:underline">question2abhi@gmail.com</a></p>
                  <p><strong>App:</strong> Speakify for Shopify</p>
                  <p><strong>Developer:</strong> Vortex AI Advisory</p>
                </div>
              </div>
            </section>

            {/* Footer */}
            <section className="text-center pt-8 border-t border-gray-200">
              <p className="text-sm text-gray-600">
                By installing and using Speakify, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service.
              </p>
            </section>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default TermsOfService;
